import React, { useMemo, useState } from "react";
import { useTable, useSortBy, usePagination, useGlobalFilter } from "react-table";

import cardMonth from "../elements/card-month";
import CalendarLegend from "../elements/calendar-legend";

// ---------- i18n ----------
const dict = {
    en: {
        show: "Show",
        entries: "entries",
        search: "Search",
        pageXofY: (x, y) => `Page ${x} of ${y}`,
        first: "<<",
        prev: "<",
        next: ">",
        last: ">>",
        status: "Status",
        responseCode: "Response Code",
        location: "Location",
        time: "Time (s)",
        tested: "Tested",
        success: "Success",
        failed: "Failed",
        error: "Error",
    },
    ru: {
        show: "Показать",
        entries: "записей",
        search: "Поиск",
        pageXofY: (x, y) => `Страница ${x} из ${y}`,
        first: "<<",
        prev: "<",
        next: ">",
        last: ">>",
        status: "Статус",
        responseCode: "Код ответа",
        location: "Локация",
        time: "Время (с)",
        tested: "Проверено",
        success: "Успех",
        failed: "Ошибка",
        error: "Ошибка",
    },
    ua: {
        show: "Показати",
        entries: "записів",
        search: "Пошук",
        pageXofY: (x, y) => `Сторінка ${x} з ${y}`,
        first: "<<",
        prev: "<",
        next: ">",
        last: ">>",
        status: "Статус",
        responseCode: "Код відповіді",
        location: "Локація",
        time: "Час (с)",
        tested: "Перевірено",
        success: "Успіх",
        failed: "Помилка",
        error: "Помилка",
    },
};
const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

// ---------- calendar ----------
const Calendar = ({ lang, monitoringTotal, selectedDate, onSelectDate }) => {
    const now = new Date();
    const months = [-1, 0, 1].map((offset) => {
        const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
        return { year: d.getFullYear(), month: d.getMonth() };
    });

    const totalsByDate = React.useMemo(() => {
        const map = {};
        (monitoringTotal || []).forEach((item) => {
            if (!item.valid_date) return;
            if (typeof item.valid_date === "string") {
                const raw = item.valid_date;
                const parts = raw.includes("T") ? raw.split("T")[0] : raw.split(" ")[0];
                if (parts) {
                    map[parts] = item;
                    return;
                }
            }
            const d = new Date(item.valid_date);
            if (Number.isNaN(d.getTime())) return;
            const key = d.toISOString().slice(0, 10);
            map[key] = item;
        });
        return map;
    }, [monitoringTotal]);

    const colorResolver = (dateKey) => {
        const item = totalsByDate[dateKey];
        if (!item) return "#9e9e9e"; // No Data
        const uptimeVal = item.uptime ? parseFloat(String(item.uptime).replace("%", "")) : NaN;
        if (Number.isNaN(uptimeVal)) return { color: "#9e9e9e", title: "No data" };
        if (uptimeVal >= 95) return { color: "#8bc34a", title: `${item.uptime}` };
        if (uptimeVal > 50) return { color: "#ffcc00", title: `${item.uptime}` };
        return { color: "#f44336", title: `${item.uptime || uptimeVal}%` };
    };

    return (
        <div
            style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: "20px",
            }}
        >
            {months.map(({ year, month }) => (
                <React.Fragment key={`${year}-${month}`}>{cardMonth(year, month, colorResolver, onSelectDate, selectedDate)}</React.Fragment>
            ))}
        </div>
    );
};

const GlobalFilter = ({ filter, setFilter, t }) => (
    <div style={{ marginBottom: "10px" }}>
        <label>
            {t.search}:{" "}
            <input
                value={filter || ""}
                onChange={(e) => setFilter(e.target.value || undefined)}
                style={{
                    padding: "5px",
                    fontSize: "1rem",
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                }}
            />
        </label>
    </div>
);

// ---------- table ----------
const LatestTestTable = ({ lang: langProp, statuses = [], selectedDate }) => {
    const lang = langProp || getInitialLang();
    const t = useMemo(() => dict[lang] || dict.en, [lang]);

    const data = React.useMemo(() => {
        const mapped = (statuses || []).map((s) => {
            const testedKey = (() => {
                if (typeof s.created === "string") {
                    const raw = s.created;
                    const parts = raw.includes("T") ? raw.split("T")[0] : raw.split(" ")[0];
                    if (parts) return parts;
                }
                const d = new Date(s.created);
                if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
                return null;
            })();
            return {
                status: String(s.staus) === "1" ? "Success" : "Failed",
                responseCode: s.status_code,
                location: s.location || "—",
                time: Number(s.response_time || 0) / 1000,
                tested: s.created,
                testedKey,
                error: s.error_message,
            };
        });
        if (selectedDate) {
            return mapped.filter((row) => row.testedKey === selectedDate);
        }
        return mapped;
    }, [statuses, selectedDate]);

    const columns = React.useMemo(
        () => [
            {
                Header: t.status,
                accessor: "status",
                Cell: ({ value }) => {
                    const isOk = value === "Success";
                    return (
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span
                  style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: isOk ? "green" : "red",
                  }}
              ></span>
                            {isOk ? t.success : t.failed}
                        </div>
                    );
                },
            },
            { Header: t.responseCode, accessor: "responseCode" },
            { Header: t.location, accessor: "location" },
            { Header: t.time, accessor: "time" },
            {
                Header: t.tested,
                accessor: "tested",
                Cell: ({ value }) => new Date(value.replace(" ", "T")).toLocaleString(lang),
            },
            {
                Header: t.error,
                accessor: "error",
                Cell: ({ value }) => value || "—",
            },
        ],
        [t, lang]
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        setGlobalFilter,
        state: { globalFilter, pageIndex, pageSize },
        pageOptions,
        nextPage,
        previousPage,
        canNextPage,
        canPreviousPage,
        gotoPage,
        setPageSize,
    } = useTable(
        { columns, data, initialState: { pageSize: 10 } },
        useGlobalFilter,
        useSortBy,
        usePagination
    );

    return (
        <div style={{ padding: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <label>
                    {t.show}{" "}
                    <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        style={{
                            padding: "5px",
                            fontSize: "1rem",
                            border: "1px solid #ccc",
                            borderRadius: "5px",
                        }}
                    >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                    </select>{" "}
                    {t.entries}
                </label>
                <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} t={t} />
            </div>

            <table
                {...getTableProps()}
                style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    textAlign: "left",
                    border: "1px solid #ddd",
                    marginTop: "10px",
                }}
            >
                <thead>
                {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                            <th
                                {...column.getHeaderProps(column.getSortByToggleProps())}
                                style={{
                                    background: "#3f51b5",
                                    color: "white",
                                    padding: "10px",
                                    border: "1px solid #ddd",
                                }}
                            >
                                {column.render("Header")}
                                <span>
                    {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                  </span>
                            </th>
                        ))}
                    </tr>
                ))}
                </thead>

                <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                    prepareRow(row);
                    return (
                        <tr
                            {...row.getRowProps()}
                            style={{ backgroundColor: row.index % 2 === 0 ? "#f9f9f9" : "#fff" }}
                        >
                            {row.cells.map((cell) => (
                                <td
                                    {...cell.getCellProps()}
                                    style={{ padding: "10px", border: "1px solid #ddd" }}
                                >
                                    {cell.render("Cell")}
                                </td>
                            ))}
                        </tr>
                    );
                })}
                </tbody>
            </table>

            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "10px",
                }}
            >
        <span>
          {t.pageXofY(pageIndex + 1, pageOptions.length)}
        </span>
                <div>
                    <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                        {t.first}
                    </button>{" "}
                    <button onClick={previousPage} disabled={!canPreviousPage}>
                        {t.prev}
                    </button>{" "}
                    <button onClick={nextPage} disabled={!canNextPage}>
                        {t.next}
                    </button>{" "}
                    <button onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage}>
                        {t.last}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ---------- page ----------
const Tests = ({ lang: langProp, singleArgs }) => {
    const lang = langProp || getInitialLang();
    const statuses = singleArgs?.statuses || [];
    const monitoringTotal = singleArgs?.monitoringTotal || [];
    const [selectedDate, setSelectedDate] = useState(null);

    const handleSelectDate = (dateKey) => {
        setSelectedDate((prev) => (prev === dateKey ? null : dateKey));
    };

    return (
        <>
            <CalendarLegend lang={lang} />
            <Calendar lang={lang} monitoringTotal={monitoringTotal} selectedDate={selectedDate} onSelectDate={handleSelectDate} />
            <LatestTestTable lang={lang} statuses={statuses} selectedDate={selectedDate} />
        </>
    );
};

export default Tests;
